import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Settings2 } from "lucide-react";
import {
  OPERATOR_ADDONS,
  PM_ADDONS,
  isModuleKey,
  moduleLabel,
} from "@shared/modular-pricing";
import { formatUsd } from "@/components/pricing/plan-selection";

export interface AccountPlanSummary {
  accountKind: "operator" | "property_manager";
  planTier: "essentials" | "pro";
  planLabel: string;
  modules: string[];
  revenueAddon: boolean;
  researchAddon: boolean;
  cpmListing: boolean;
  roomsOnPadsplit: number;
  roomsOffPadsplit: number;
  combinedRooms: number;
  portalOwners: number;
  waivedOwners: number;
  offMarketplaceVendors: number;
  quote: { totalCents: number; volumeDiscountPercent: number };
  trial: { endsAt: string | null; active: boolean; daysLeft: number };
  subscriptionStatus: string;
}

/**
 * The modular account's current plan at a glance — tier, active modules and
 * add-ons as chips, live room counts, and the current monthly total — with
 * the edit path pointing at the full calculator on /app/account/plan.
 */
export function CurrentPlanCard({ plan }: { plan: AccountPlanSummary }) {
  const chips: string[] = [
    ...plan.modules.filter(isModuleKey).map((m) => moduleLabel(m)),
    ...(plan.revenueAddon ? [OPERATOR_ADDONS.revenue.label] : []),
    ...(plan.researchAddon ? [OPERATOR_ADDONS.research.label] : []),
    ...(plan.cpmListing ? [PM_ADDONS.cpm_listing.label] : []),
  ];

  return (
    <Card data-testid="card-current-plan">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg">
            {plan.planLabel}
            {plan.trial.active && (
              <Badge variant="secondary" className="ml-2 bg-blue-50 text-blue-700 border-blue-200" data-testid="badge-plan-trial">
                Trial — {plan.trial.daysLeft} day{plan.trial.daysLeft === 1 ? "" : "s"} left
              </Badge>
            )}
          </CardTitle>
          <span className="text-2xl font-extrabold" data-testid="text-plan-total">
            {formatUsd(plan.quote.totalCents)}
            <span className="text-sm font-normal text-muted-foreground">/mo</span>
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground" data-testid="text-plan-rooms">
          {plan.roomsOnPadsplit.toLocaleString("en-US")} room{plan.roomsOnPadsplit === 1 ? "" : "s"} on PadSplit
          {plan.roomsOffPadsplit > 0 && <> · {plan.roomsOffPadsplit.toLocaleString("en-US")} off PadSplit</>}
          {plan.accountKind === "property_manager" && (
            <>
              {" "}· {Math.max(0, plan.portalOwners - plan.waivedOwners)} billed owner portal
              {Math.max(0, plan.portalOwners - plan.waivedOwners) === 1 ? "" : "s"}
              {plan.waivedOwners > 0 && <> ({plan.waivedOwners} waived)</>}
            </>
          )}
          {plan.offMarketplaceVendors > 0 && <> · {plan.offMarketplaceVendors} private vendor{plan.offMarketplaceVendors === 1 ? "" : "s"}</>}
          {plan.quote.volumeDiscountPercent > 0 && <> · {plan.quote.volumeDiscountPercent}% volume discount</>}
        </p>
        {plan.planTier === "pro" ? (
          <p className="text-sm text-muted-foreground">Every module and device integration is included in Pro.</p>
        ) : chips.length > 0 ? (
          <div className="flex flex-wrap gap-1.5" data-testid="list-plan-module-chips">
            {chips.map((chip) => (
              <Badge key={chip} variant="secondary">{chip}</Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No modules added yet — Essentials core only.</p>
        )}
        <Link href="/app/account/plan">
          <Button variant="outline" size="sm" data-testid="button-edit-plan">
            <Settings2 size={14} className="mr-1.5" /> Edit plan & modules
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
